import Head from 'next/head';
import React from 'react';
import { Box, Container } from '@mui/material';
import BaseLayout from 'layouts/BaseLayout';
import Header from 'components/Templates/Header';
import List from 'components/Templates/List';
import AboutSection from 'components/Templates/AboutSection';
import FormatsService from 'services/FormatsService';
import TemplatesService from 'services/TemplatesService';
import { COLOR_PURPLE_HOVER, COLOR_WHITE } from 'styles/variables/colors';
import useIsMobile from 'hooks/useIsMobile';

function getFormatListData(slug) {
  const data = {
    slides: {
      columns: 5,
      perPage: 15,
    },
    abstracts: {
      columns: 3,
      perPage: 9,
    },
    infographics: {
      columns: 6,
      perPage: 18,
    },
    posters: {
      columns: 5,
      perPage: 15,
    },
  };

  if (data[slug]) {
    return data[slug];
  }

  throw new Error(`Unable to get format list data for unknown format [${slug}].`);
}

export async function getStaticProps() {
  try {
    const { data: formats } = await FormatsService.fetchAll();

    for (const format of formats) {
      let perPage;

      switch (format.slug) {
        case 'slides': {
          perPage = 4;
          break;
        }
        case 'abstracts': {
          perPage = 2;
          break;
        }
        case 'infographics': {
          perPage = 5;
          break;
        }
        case 'posters': {
          perPage = 4;
          break;
        }
        default: {
          throw new Error(
            `Unable to determine "perPage" value for unknown format [${format.slug}].`,
          );
        }
      }

      const { data: templates } = await TemplatesService.fetchAll({
        formatId: format.id,
        perPage,
        neutral: true,
      });

      format.templates = templates;
    }

    return {
      props: { formats },
      revalidate: 604800, // 1 week
    };
  } catch (e) {
    console.error(e);

    return {
      props: { formats: [] },
      revalidate: 604800, // 1 week
    };
  }
}

export default function NeutralTemplatesPage({ formats }) {
  const isMobile = useIsMobile();

  return (
    <BaseLayout
      bgBrandColor={!isMobile ? COLOR_WHITE : COLOR_PURPLE_HOVER}
      headerWhite={true}
      typeBtn="buttonSecondary"
      hasPadding={false}
    >
      <Head>
        <title>Neutral Infographic templates - Mind the Graph</title>
      </Head>

      <Header />

      <Box py={6} bgcolor="common.white">
        <Container>
          {formats.map((format, index) => (
            <Box key={index} mb={index !== formats.length - 1 ? 4 : 0}>
              <List
                key={index}
                breadcrumbItems={[
                  {
                    label: format.name,
                    path: `/infographic-templates/category/neutral/format/${format.slug}`,
                  },
                ]}
                formatName={`Neutral ${format.name}`}
                templates={format.templates}
                columns={getFormatListData(format.slug).columns}
                expandButtonUrl={`/infographic-templates/category/neutral/format/${format.slug}`}
              />
            </Box>
          ))}
        </Container>
      </Box>

      <AboutSection />
    </BaseLayout>
  );
}
